/**
 * ThermalStatus.js
 *
 * Indicador visual de estado de la impresora BLE + botón de acción.
 *
 * Comportamiento:
 *   - Conectada   → bolita verde, "Impresora lista", botón "Imprimir BT"
 *   - Conectando  → spinner, "Conectando…"
 *   - Desconectada → bolita roja, "Sin impresora", botón "Conectar"
 *
 * Si se pasa `onPrint`, al tocar "Imprimir BT":
 *   1. Si ya está conectada → imprime directo
 *   2. Si no → conecta primero, y al conectar imprime
 */

import React, { useState } from "react";
import { useThermalPrinter } from "./ThermalPrinterContext";
import { ToastStore } from "react-toasts";

export function ThermalStatus({ onPrint, className = "" }) {
    const { connected, connecting, error, connect } = useThermalPrinter();

    const handleConnect = async (e) => {
        e.preventDefault();
        try {
            await connect();
        } catch (_) {}
    };

    return (
        <div
            className={`d-flex align-items-center ${className}`}
            style={{ gap: 8 }}
        >
            {/* Indicador */}
            <span
                style={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    backgroundColor: connecting
                        ? "#F59E0B"
                        : connected
                        ? "#10B981"
                        : "#EF4444",
                    display: "inline-block",
                    flexShrink: 0,
                }}
            />
            <span style={{ fontSize: 12, color: "#667085", whiteSpace: "nowrap" }}>
                {connecting
                    ? "Conectando…"
                    : connected
                    ? "Impresora BT"
                    : "Sin impresora"}
            </span>

            {/* Botón de acción */}
            {!connected && !connecting && (
                <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    style={{ fontSize: 11, padding: "2px 8px", borderRadius: 6 }}
                    onClick={handleConnect}
                >
                    Conectar
                </button>
            )}

            {error && (
                <span style={{ fontSize: 11, color: "#EF4444" }}>{error}</span>
            )}
        </div>
    );
}

/**
 * Botón de impresión BLE para poner junto a los botones existentes.
 *
 * Si la impresora no está conectada, primero conecta.
 * `buildDoc` es una función que retorna el doc (Uint8Array) a imprimir.
 */
export function ThermalPrintButton({ buildDoc, label = "Imprimir BT", className = "" }) {
    const { connected, connecting, connect, print } = useThermalPrinter();
    const [printing, setPrinting] = useState(false);

    const handleClick = async (e) => {
        e.preventDefault();
        setPrinting(true);

        try {
            // Si no está conectada, conectar primero
            if (!connected) {
                await connect();
            }

            // Generar documento e imprimir
            const doc = await buildDoc();
            if (doc) {
                await print(doc);
                ToastStore.success("Impresión enviada");
            }
        } catch (err) {
            if (err && err.name !== "NotFoundError") {
                ToastStore.error(err.message || "Error al imprimir");
            }
        } finally {
            setPrinting(false);
        }
    };

    const isLoading = connecting || printing;

    return (
        <button
            type="button"
            className={`btn btn-info ${className}`}
            onClick={handleClick}
            disabled={isLoading}
        >
            {isLoading ? (
                <em className="fa fa-spinner fa-spin" />
            ) : (
                <em className="fa fa-bluetooth" />
            )}{" "}
            {isLoading ? "Enviando…" : label}
        </button>
    );
}

export default ThermalStatus;